export type E164PhoneNumber = `+${number}`;
export type ShortCode = `${number}`;
export type AlphanumericSenderId = string;

export function convertArbitraryPhoneNumberToE164(
  phoneNumber: string,
  defaultCountryCode: string = "+1",
): E164PhoneNumber | null {
  const sanitizedPhoneNumber = phoneNumber.replace(/[^\d+]/g, "");
  if (sanitizedPhoneNumber.startsWith("+")) {
    return sanitizedPhoneNumber as E164PhoneNumber;
  } else if (
    sanitizedPhoneNumber.length === 11 &&
    sanitizedPhoneNumber.startsWith("1")
  ) {
    return `+${sanitizedPhoneNumber}` as E164PhoneNumber;
  } else {
    const e164Number = `${defaultCountryCode}${sanitizedPhoneNumber}`;
    const digitsOnly = e164Number.replace(/\D/g, "");
    if (digitsOnly.length >= 10 && digitsOnly.length <= 15) {
      return e164Number as E164PhoneNumber;
    } else {
      return null;
    }
  }
}

export function isValidE164PhoneNumber(phoneNumber: string): boolean {
  return /^\+[1-9]\d{7,14}$/.test(phoneNumber);
}

export function convertArbitraryPhoneNumberToDigits(
  phoneNumber: string,
  defaultCountryCode: string = "+1",
): string | null {
  const e164Number = convertArbitraryPhoneNumberToE164(
    phoneNumber,
    defaultCountryCode,
  );
  if (e164Number) {
    return e164Number.replace(defaultCountryCode, "");
  }
  return null;
}

export function createSecureSmsUri(params: {
  phoneNumbers?: string[];
  body?: string;
}): string | null {
  const { phoneNumbers, body } = params;

  // Validate and sanitize phone numbers
  const validatedNumbers: string[] = [];
  if (phoneNumbers && phoneNumbers.length > 0) {
    for (const number of phoneNumbers) {
      if (!number || typeof number !== "string") {
        continue;
      }

      // Remove any potentially dangerous characters and validate
      const sanitizedNumber = number.replace(/[^\d+,]/g, "");

      // Check if it's a valid phone number format
      // Allow formats like +1234567890, 1234567890, etc.
      if (
        /^[\d+,]+$/.test(sanitizedNumber) &&
        sanitizedNumber.length >= 10 &&
        sanitizedNumber.length <= 20
      ) {
        validatedNumbers.push(sanitizedNumber);
      }
    }

    if (validatedNumbers.length === 0) {
      return null;
    }
  }

  // Sanitize body text - ensure it's properly encoded and safe
  let encodedBody = "";
  if (body && typeof body === "string") {
    // Remove any control characters and ensure proper encoding
    const sanitizedBody = body
      .replace(/[\x00-\x1F\x7F]/g, "") // Remove control characters
      .substring(0, 1000); // Limit length to prevent abuse

    encodedBody = encodeURIComponent(sanitizedBody);
  }

  // Build the SMS URI with platform-specific formatting
  const numbersString = validatedNumbers.join(",");

  if (encodedBody) {
    // Detect iOS for proper SMS URI formatting
    const isIOS =
      typeof navigator !== "undefined" &&
      /iPhone|iPad|iPod/.test(navigator.userAgent) &&
      ("ontouchend" in document || (navigator as any).maxTouchPoints > 1);

    // iOS uses & separator, other platforms use ?
    const separator = isIOS ? "&" : "?";
    return `sms:${numbersString}${separator}body=${encodedBody}`;
  }

  return `sms:${numbersString}`;
}
